import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  MessageCircle, QrCode, Clock, CheckCircle, AlertTriangle,
  RefreshCw, Wifi, Send, Loader2, Inbox, XCircle,
  Settings2, Zap, Megaphone, FileText, ChevronDown, ChevronUp, RotateCcw, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getAuthAxios } from '@/store';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useI18n } from '@/components/I18nProvider';

const formatDate = (d) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('tr-TR') + ' ' + new Date(d).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
};

const AUTOMATION_LABELS = {
  welcome_enabled: { label: 'Hoş Geldiniz Mesajı', desc: 'Yeni kayıt sonrası aboneye gönderilir', icon: MessageCircle },
  trial_start_enabled: { label: 'Deneme Başladı Bilgisi', desc: 'Deneme süresi başladığında bildirim', icon: Clock },
  trial_expiry_warning_enabled: { label: 'Deneme Bitiyor Uyarısı', desc: '2 gün kala uyarı gönderilir', icon: AlertTriangle },
  subscription_activated_enabled: { label: 'Abonelik Başlayınca Bilgi', desc: 'Abonelik aktifleştirildiğinde bildirim', icon: CheckCircle },
};

const msgTypeLabel = (type) => {
  const labels = {
    admin_test: 'Test', broadcast: 'Duyuru',
    auto_welcome: 'Hoş Geldiniz', auto_trial_start: 'Deneme Başladı',
    auto_trial_expiry_warning: 'Deneme Uyarısı', auto_subscription_activated: 'Abonelik Aktif',
  };
  return labels[type] || type;
};

export default function AdminWhatsAppPage() {
  const { t } = useI18n();
  const [status, setStatus] = useState('disconnected');
  const [qrCode, setQrCode] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [pollInterval, setPollInterval] = useState(null);

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [sendForm, setSendForm] = useState({ phone: '', message: '' });
  const [isSending, setIsSending] = useState(false);

  // Broadcast
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastTarget, setBroadcastTarget] = useState('all');
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [targets, setTargets] = useState({ all: 0, active: 0, trial: 0 });

  // Messages
  const [messages, setMessages] = useState([]);
  const [msgStats, setMsgStats] = useState({ total_sent: 0, total_failed: 0, today_sent: 0, today_failed: 0, pending_in_queue: 0 });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/whatsapp/status');
      setStatus(res.data.status);
      setQrCode(res.data.qr);
      if (res.data.status === 'connected' && pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch (e) { console.error(e); }
  }, [pollInterval]);

  const fetchAll = useCallback(async () => {
    try {
      const api = getAuthAxios();
      const [sRes, mRes, stRes, tgRes, tmRes] = await Promise.all([
        api.get('/admin/whatsapp/automation-settings'),
        api.get('/admin/whatsapp/messages?limit=50'),
        api.get('/admin/whatsapp/message-stats'),
        api.get('/admin/whatsapp/broadcast-targets'),
        api.get('/admin/whatsapp/templates'),
      ]);
      setSettings(sRes.data);
      setMessages(mRes.data);
      setMsgStats(stRes.data);
      setTargets(tgRes.data);
      setTemplates(tmRes.data);
    } catch (e) { console.error(e); }
    finally { setSettingsLoading(false); }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchAll();
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const api = getAuthAxios();
      const res = await api.post('/admin/whatsapp/connect');
      if (res.data.status === 'connected') {
        setStatus('connected'); setQrCode(null);
        toast.success('WhatsApp bağlandı!');
      } else if (res.data.qr) {
        setQrCode(res.data.qr); setStatus('waiting_for_scan');
        toast.info('QR kodu telefonunuzla tarayın');
        const iv = setInterval(fetchStatus, 3000);
        setPollInterval(iv);
      } else {
        toast.info('QR kod oluşturuluyor...');
        setTimeout(async () => {
          const retry = await api.get('/admin/whatsapp/status');
          if (retry.data.qr) {
            setQrCode(retry.data.qr); setStatus('waiting_for_scan');
            setPollInterval(setInterval(fetchStatus, 3000));
          }
        }, 2000);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Bağlantı hatası'); }
    finally { setIsConnecting(false); }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      const api = getAuthAxios();
      await api.post('/admin/whatsapp/disconnect');
      setStatus('disconnected'); setQrCode(null);
      toast.success(t('disconnected'));
      if (pollInterval) { clearInterval(pollInterval); setPollInterval(null); }
    } catch (e) { toast.error('Bağlantı kesme hatası'); }
    finally { setIsDisconnecting(false); }
  };

  const handleToggle = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    setSaving(true);
    try {
      const api = getAuthAxios();
      await api.put('/admin/whatsapp/automation-settings', { [key]: value });
    } catch (e) { setSettings(settings); toast.error('Ayar güncellenemedi'); }
    finally { setSaving(false); }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    if (!sendForm.phone || !sendForm.message) return;
    setIsSending(true);
    try {
      const api = getAuthAxios();
      await api.post('/admin/whatsapp/send-test', sendForm);
      toast.success('Test mesajı kuyruğa alındı');
      setSendForm({ phone: '', message: '' });
      setTimeout(fetchAll, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Gönderme hatası'); }
    finally { setIsSending(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastMsg.trim()) { toast.error('Mesaj içeriği gerekli'); return; }
    setIsBroadcasting(true);
    try {
      const api = getAuthAxios();
      const res = await api.post('/admin/whatsapp/broadcast', { message: broadcastMsg, target: broadcastTarget });
      toast.success(`${res.data.queued} aboneye mesaj kuyruğa alındı${res.data.skipped > 0 ? ` (${res.data.skipped} telefonsuz atlandı)` : ''}`);
      setBroadcastMsg('');
      setTimeout(fetchAll, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Duyuru gönderilemedi'); }
    finally { setIsBroadcasting(false); }
  };

  const handleExpandTemplate = (ttype) => {
    if (expandedTemplate === ttype) { setExpandedTemplate(null); return; }
    setExpandedTemplate(ttype);
    const tmpl = templates.find(t => t.template_type === ttype);
    setEditingContent(tmpl?.content || '');
  };

  const handleSaveTemplate = async (ttype) => {
    setSavingTemplate(ttype);
    try {
      const api = getAuthAxios();
      await api.put(`/admin/whatsapp/templates/${ttype}`, { content: editingContent });
      toast.success(t('templateSaved'));
      const res = await api.get('/admin/whatsapp/templates');
      setTemplates(res.data);
      setExpandedTemplate(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Şablon kaydedilemedi'); }
    finally { setSavingTemplate(null); }
  };

  const handleResetTemplate = async (ttype) => {
    setSavingTemplate(ttype);
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/whatsapp/templates/${ttype}`);
      toast.success('Şablon varsayılana sıfırlandı');
      const res = await api.get('/admin/whatsapp/templates');
      setTemplates(res.data);
      setExpandedTemplate(null);
    } catch (e) { toast.error('Şablon sıfırlanamadı'); }
    finally { setSavingTemplate(null); }
  };

  const isConnected = status === 'connected';
  const targetCount = broadcastTarget === 'active' ? targets.active : broadcastTarget === 'trial' ? targets.trial : targets.all;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-whatsapp-page">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">WhatsApp Yönetimi</h1>
        <p className="mt-1 text-sm text-muted-foreground">Bağlantı, duyuru, otomasyon ve mesaj şablonları</p>
      </div>

      {/* Connection + Test */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card data-testid="connection-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              {isConnected ? <Wifi className="h-5 w-5 text-green-500" /> : <QrCode className="h-5 w-5 text-primary" />}
              WhatsApp Bağlantısı
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            {isConnected ? (
              <div className="flex flex-col items-center gap-4 py-4">
                <div className="rounded-full bg-green-500/10 p-4"><CheckCircle className="h-12 w-12 text-green-500" /></div>
                <p className="text-lg font-medium text-green-600">WhatsApp Bağlı</p>
                <p className="text-sm text-muted-foreground text-center">Sistem aktif, mesajlar gönderilebilir</p>
                <Button variant="outline" onClick={handleDisconnect} disabled={isDisconnecting} data-testid="disconnect-btn">
                  {isDisconnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Kesiliyor...</> : t('disconnect')}
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                {qrCode ? (
                  <div className="rounded-xl border-4 border-green-200/40 p-4 bg-white">
                    <img src={qrCode} alt="QR Code" className="h-48 w-48 sm:h-56 sm:w-56" data-testid="qr-code-image" />
                  </div>
                ) : (
                  <div className="flex h-48 w-48 sm:h-56 sm:w-56 items-center justify-center rounded-xl border-4 border-dashed border-muted">
                    <p className="text-center text-sm text-muted-foreground px-4">QR kod oluşturmak için<br />butona tıklayın</p>
                  </div>
                )}
                {status === 'waiting_for_scan' && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><RefreshCw className="h-4 w-4 animate-spin" />Tarama bekleniyor...</div>
                )}
                <Button onClick={handleConnect} disabled={isConnecting} data-testid="connect-btn" className="bg-green-600 hover:bg-green-700">
                  {isConnecting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Bağlanıyor...</> : qrCode ? <><RefreshCw className="mr-2 h-4 w-4" />Yeni QR Kod</> : <><QrCode className="mr-2 h-4 w-4" />QR Kod Oluştur</>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {isConnected ? (
          <Card data-testid="test-message-card">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg"><Send className="h-5 w-5 text-green-500" />Test Mesajı</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendTest} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('phone')}</Label>
                  <Input placeholder="905xxxxxxxxx" value={sendForm.phone} onChange={(e) => setSendForm({ ...sendForm, phone: e.target.value })} data-testid="test-phone-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{t('messageText')}</Label>
                  <Textarea placeholder="Mesajınızı yazın..." value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} rows={3} data-testid="test-message-input" />
                </div>
                <Button type="submit" disabled={isSending || !sendForm.phone || !sendForm.message} className="w-full bg-green-600 hover:bg-green-700" data-testid="test-send-btn">
                  {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}Gönder
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-lg">Nasıl Bağlanılır?</CardTitle></CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {['WhatsApp\'ı açın', 'Ayarlar > Bağlı Cihazlar', '"Cihaz Bağla" butonuna tıklayın', 'QR kodu tarayın'].map((s, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-600 text-sm font-medium text-white">{i + 1}</span>
                    <span className="text-sm">{s}</span>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Broadcast */}
      <Card data-testid="broadcast-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Megaphone className="h-5 w-5 text-blue-500" />Toplu Duyuru
          </CardTitle>
          <CardDescription>Tüm abonelere veya belirli gruba {t('sendWhatsApp')}in</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'Tüm Aboneler', count: targets.all },
              { value: 'active', label: 'Aktif Aboneler', count: targets.active },
              { value: 'trial', label: 'Deneme Süresi', count: targets.trial },
            ].map(({ value, label, count }) => (
              <button
                key={value}
                onClick={() => setBroadcastTarget(value)}
                className={cn(
                  'flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm transition-all',
                  broadcastTarget === value ? 'border-primary bg-primary/5 ring-1 ring-primary/20 font-medium' : 'hover:bg-muted/50'
                )}
                data-testid={`target-${value}`}
              >
                <Users className="h-4 w-4" />
                {label}
                <Badge variant="outline" className="text-[10px] px-1.5">{count}</Badge>
              </button>
            ))}
          </div>
          <Textarea
            placeholder="Duyuru mesajınızı yazın... (*kalın*, _italic_ kullanabilirsiniz)"
            value={broadcastMsg}
            onChange={(e) => setBroadcastMsg(e.target.value)}
            rows={4}
            data-testid="broadcast-message-input"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">{targetCount}</span> aboneye gönderilecek
            </p>
            <Button
              onClick={handleBroadcast}
              disabled={isBroadcasting || !broadcastMsg.trim() || targetCount === 0}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="broadcast-send-btn"
            >
              {isBroadcasting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Gönderiliyor...</> : <><Megaphone className="mr-2 h-4 w-4" />Duyuru Gönder</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Automation Toggles */}
      <Card data-testid="automation-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><Settings2 className="h-5 w-5 text-amber-500" />Otomatik Mesaj Ayarları</CardTitle>
          <CardDescription>Yeni abone olanlara otomatik gönderilecek mesajlar</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1">
          {settingsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            Object.entries(AUTOMATION_LABELS).map(([key, { label, desc, icon: Icon }]) => (
              <div key={key} className="flex items-center justify-between rounded-lg border p-3 sm:p-4 transition-colors hover:bg-muted/30" data-testid={`toggle-${key}`}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="shrink-0 rounded-lg bg-muted p-2"><Icon className="h-4 w-4 text-muted-foreground" /></div>
                  <div className="min-w-0"><p className="text-sm font-medium">{label}</p><p className="text-xs text-muted-foreground hidden sm:block">{desc}</p></div>
                </div>
                <Switch checked={settings?.[key] ?? true} onCheckedChange={(v) => handleToggle(key, v)} disabled={saving} data-testid={`switch-${key}`} />
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Message Queue */}
      <Card data-testid="message-queue-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg"><Inbox className="h-5 w-5 text-primary" />Mesaj Kuyruğu</CardTitle>
              <CardDescription>Gönderilen ve bekleyen mesajlar</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchAll} data-testid="refresh-messages-btn"><RefreshCw className="mr-1.5 h-4 w-4" />{t('refresh')}</Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4 mb-4">
            {[
              { icon: Clock, color: 'text-amber-500', label: 'Kuyrukta', val: msgStats.pending_in_queue },
              { icon: CheckCircle, color: 'text-green-500', label: 'Gönderilen', val: `${msgStats.total_sent} (${msgStats.today_sent})` },
              { icon: XCircle, color: 'text-red-500', label: 'Hatalı', val: `${msgStats.total_failed} (${msgStats.today_failed})` },
              { icon: MessageCircle, color: 'text-blue-500', label: 'Toplam', val: messages.length },
            ].map(({ icon: I, color, label, val }, idx) => (
              <div key={idx} className="flex items-center gap-2 rounded-lg border p-3">
                <I className={cn('h-4 w-4', color)} />
                <div><p className="text-[11px] text-muted-foreground">{label}</p><p className="font-bold text-sm">{val}</p></div>
              </div>
            ))}
          </div>
          {messages.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              <Inbox className="mx-auto mb-3 h-10 w-10 opacity-30" />
              <p>Henüz mesaj yok</p>
            </div>
          ) : (
            <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1">
              {messages.map((msg) => (
                <div key={msg.id} className={cn('rounded-lg border p-3', msg.status === 'sent' && 'bg-green-50/50 border-green-200/60', msg.status === 'failed' && 'bg-red-50/50 border-red-200/60', msg.status === 'pending' && 'bg-amber-50/50 border-amber-200/60')} data-testid={`msg-${msg.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{msg.phone}</span>
                        <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', msg.status === 'sent' && 'border-green-300 text-green-700 bg-green-100', msg.status === 'failed' && 'border-red-300 text-red-700 bg-red-100', msg.status === 'pending' && 'border-amber-300 text-amber-700 bg-amber-100')}>
                          {msg.status === 'sent' ? 'Gönderildi' : msg.status === 'failed' ? 'Hata' : 'Beklemede'}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{msgTypeLabel(msg.message_type)}</Badge>
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{msg.message_content}</p>
                      {msg.error_message && <p className="mt-1 text-xs text-destructive flex items-center gap-1"><AlertTriangle className="h-3 w-3 shrink-0" />{msg.error_message}</p>}
                    </div>
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap shrink-0">{formatDate(msg.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Message Templates */}
      <Card data-testid="templates-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg"><FileText className="h-5 w-5 text-primary" />Mesaj Şablonları</CardTitle>
          <CardDescription>Otomatik mesajların içeriklerini düzenleyin. Değişkenler {'{ }'} ile kullanılır.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {templates.map((tmpl) => {
            const isExpanded = expandedTemplate === tmpl.template_type;
            return (
              <div key={tmpl.template_type} className="rounded-lg border transition-all" data-testid={`template-${tmpl.template_type}`}>
                <button
                  className="flex w-full items-center justify-between p-3 sm:p-4 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => handleExpandTemplate(tmpl.template_type)}
                  data-testid={`template-btn-${tmpl.template_type}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-sm font-medium">{tmpl.name}</span>
                    {tmpl.is_default ? (
                      <Badge variant="outline" className="text-[10px] px-1.5">Varsayılan</Badge>
                    ) : (
                      <Badge className="text-[10px] px-1.5 bg-primary/10 text-primary border-primary/20">Özel</Badge>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
                </button>
                {isExpanded && (
                  <div className="border-t p-3 sm:p-4 space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground mb-1.5 block">
                        Değişkenler: {tmpl.variables?.map(v => `{${v}}`).join(', ')}
                      </Label>
                      <Textarea value={editingContent} onChange={(e) => setEditingContent(e.target.value)} rows={6} className="font-mono text-sm" data-testid={`editor-${tmpl.template_type}`} />
                    </div>
                    <div className="flex items-center gap-2 justify-end">
                      {!tmpl.is_default && (
                        <Button variant="outline" size="sm" onClick={() => handleResetTemplate(tmpl.template_type)} disabled={savingTemplate === tmpl.template_type} data-testid={`reset-${tmpl.template_type}`}>
                          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />Varsayılana Dön
                        </Button>
                      )}
                      <Button size="sm" onClick={() => handleSaveTemplate(tmpl.template_type)} disabled={savingTemplate === tmpl.template_type || !editingContent.trim()} data-testid={`save-${tmpl.template_type}`}>
                        {savingTemplate === tmpl.template_type ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="mr-1.5 h-3.5 w-3.5" />}Kaydet
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
